import { memo, useEffect, useMemo} from "react";
import { getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { selectOrderBooks, selectSnapshot, selectSnapshots } from "../../selectors/security";
import { toLocaleStringIn } from "../../util/general";
import { AnalyzeGroupedPosition } from "../../reducer/positions.reducer";
import { selectOrderType, selectTradeOrders } from "../../selectors/strategyBuilder";
import { selectOrdersHistory } from "../../selectors/orders";
import { Broker_OrderType, OrderStatus, TransactionType } from "../../proto/generated/order";
import { calculateGcd } from "../../util/orders";

export type LtpCellProps = {
    position: AnalyzeGroupedPosition
}

const LtpCell: React.FC<LtpCellProps> = ({position}) => {

    const { security, newQuantity } = position;
    const securityKey = getSecurityKey(security.exchange, security.code);
    const snapshots = useSelector(selectSnapshots);
    const orderBooks = useSelector(selectOrderBooks);
    const snapshot = useSelector(selectSnapshot(securityKey))
    const tradeOrders = useSelector(selectTradeOrders);
    const placedOrders = useSelector(selectOrdersHistory);
    const orderType = useSelector(selectOrderType);

    const basketLTP = useMemo(() => {
        if ( !snapshots || position.children?.length <= 1) return 0;

        // Build orderIdToOrderMap for filtering
        const orderIdToOrderMap: Record<string, any> = {};
        placedOrders.forEach(order => {
            if (order.orderId) {
                orderIdToOrderMap[order.orderId] = order;
            }
        });

        // Track which securities have been processed (have existing positions)
        const processedSecurities = new Set<string>();
        position.children?.forEach(p => {
            processedSecurities.add(p.security.code);
        });

        // Filter new orders (orders without existing positions)
        const newOrders = tradeOrders.filter(order => {
            const isSelected = order.selected;
            const isNotExecuted = !order.referenceOrderId || orderIdToOrderMap[order.referenceOrderId]?.status !== OrderStatus.EXECUTED;
            const isNewSecurity = !processedSecurities.has(order.security.code);
            return isSelected && isNotExecuted && isNewSecurity;
        });

        // Calculate LTP from existing positions
        let ltp = position.children?.reduce((sum, p) => {
            const snapshot = snapshots[getSecurityKey(p.security.exchange, p.security.code)];
            // Check if there's a square off order for this position
            const squareOffOrder = p.squareOffOrderId
                ? tradeOrders?.find(t => t.clientOrderId === p.squareOffOrderId)
                : null;

            if (squareOffOrder) {
                const remainingQty = p.newQuantity ?? 0;
                if (!remainingQty) return sum; // Fully squared off — no LTP contribution
                return sum + (snapshot?.lastTradedPrice || 0) * remainingQty;
            }

            const effectiveQuantity = p.newQuantity !== undefined && p.newQuantity !== 0 ? p.newQuantity : p.quantity

            // For positions without square off orders, always use live LTP
            return sum + (snapshot?.lastTradedPrice || 0) * effectiveQuantity
        }, 0) || 0;

        // Add LTP contribution from new orders
        newOrders.forEach(order => {
            const orderSnapshot = snapshots[getSecurityKey(order.security.exchange, order.security.code)];
            const direction = order.transactionType === TransactionType.BUY ? 1 : -1;
            const orderQuantity = direction * order.quantity;

            // If order has a non-zero price and we're in LIMIT mode, use the fixed price
            // If in MARKET mode, use live LTP
            const priceToUse = (order.price && order.price !== 0 && orderType === Broker_OrderType.LIMIT_ORDER)
                ? order.price
                : (orderSnapshot?.lastTradedPrice || 0);

            ltp += priceToUse * orderQuantity;
        });

        return ltp;
    }, [position, snapshots, orderBooks, tradeOrders, placedOrders, orderType])

    // Calculate effective quantity from children when basket quantity is 0 (all squared off)
    const effectiveQuantity = useMemo(() => {
        if (newQuantity !== undefined && newQuantity !== 0) {
            return newQuantity
        }
        if (position.quantity !== 0) {
            return position.quantity
        }

        // When all positions are squared off, calculate from original positions
        const allPositions = position.children || []
        if (!allPositions.length) return 0

        const quantities = allPositions.map(p => Math.abs(p.quantity)).sort()
        return allPositions.length > 1 ? calculateGcd(quantities) : quantities[0] || 0
    }, [newQuantity, position.quantity, position.children])

    const displayLTP = useMemo(() => {
        // For baskets (multiple children), use the calculated basketLTP which respects orderType
        if (position.children?.length > 1) {
            if (effectiveQuantity === 0) return 0;
            return Math.abs(basketLTP/effectiveQuantity);
        }

        // For single positions, check if there's a square off order first
        const squareOffOrder = position.squareOffOrderId
            ? tradeOrders?.find(t => t.clientOrderId === position.squareOffOrderId)
            : null;

        const orderBook = orderBooks[securityKey];

        // If position is completely squared off (newQuantity === 0), show the square off price
        if (newQuantity === 0 && squareOffOrder && squareOffOrder.selected) {
            // LIMIT: use the fixed order price (set once, never changes)
            if (squareOffOrder.price && squareOffOrder.price !== 0 && orderType === Broker_OrderType.LIMIT_ORDER) {
                return squareOffOrder.price;
            }
            const ltp = snapshot?.lastTradedPrice ?? 0;
            if (!position.originalPosition) {
                return ltp;
            }
            // MARKET: use live bid/ask — bid when closing a long (selling), ask when closing a short (buying)
            return position.originalPosition?.closePosition > 0
                ? (orderBook?.bids[0]?.price ?? snapshot?.lastTradedPrice ?? 0)
                : (orderBook?.asks[0]?.price ?? snapshot?.lastTradedPrice ?? 0);
        }

        // If there's a square off order with a price and we're in LIMIT mode, use the fixed price
        if (squareOffOrder && squareOffOrder.price && squareOffOrder.price !== 0 && orderType === Broker_OrderType.LIMIT_ORDER) {
            return squareOffOrder.price;
        }

        // If there's a square off order in MARKET mode, use order book price
        if (squareOffOrder && squareOffOrder.selected && orderType === Broker_OrderType.MARKET_ORDER) {
            return position.originalPosition.closePosition > 0
                ? (orderBook?.bids[0]?.price ?? snapshot?.lastTradedPrice ?? 0)
                : (orderBook?.asks[0]?.price ?? snapshot?.lastTradedPrice ?? 0);
        }

        // Otherwise show live LTP
        return snapshot?.lastTradedPrice ?? 0;
    }, [newQuantity, effectiveQuantity, position.children, position.squareOffOrderId, basketLTP, tradeOrders, orderType, snapshot, orderBooks, securityKey, position.originalPosition])

    // Use priceMultiplier from security, or default to 100 (standard for most securities)
    const priceMultiplier = security.priceMultiplier ?? 100

    // For completely squared off positions, always show the square off price (not "-")
    if (newQuantity === 0 && position.squareOffOrderId) {
        return <span>{toLocaleStringIn(displayLTP/priceMultiplier)}</span>;
    }

    return <span>{ effectiveQuantity !== 0 ? toLocaleStringIn(displayLTP/priceMultiplier) : "-"}</span>;

}


export default memo(LtpCell);